import React from 'react';
import { motion } from 'framer-motion';
import { 
  Globe, 
  Layout, 
  Zap, 
  Shield, 
  Search, 
  ArrowRight,
  CheckCircle,
  Server,
  Layers,
  MessageSquare,
  PhoneCall
} from 'lucide-react';
import './WebDev.css';
import FAQAccordion from './FAQAccordion';

const webServices = [
  {
    icon: <Layout size={24} />,
    title: 'Custom Web Applications & SaaS Portals',
    desc: 'Scalable multi-tenant web applications, client dashboards, and subscription portals engineered with React, Next.js, and Node.js microservices.'
  },
  {
    icon: <Search size={24} />,
    title: 'SEO & Core Web Vitals Optimization',
    desc: 'Architected for sub-second page loads, 95+ Google PageSpeed scores, automated sitemap indexing, and Schema.org rich snippet metadata.'
  },
  {
    icon: <Zap size={24} />,
    title: 'High-Conversion Corporate & Product Sites',
    desc: 'Premium dark-mode and glassmorphic website design tailored to wow enterprise clients, drive inbound leads, and showcase complex technical offerings.'
  },
  {
    icon: <Layers size={24} />,
    title: 'Headless CMS & E-Commerce Platforms',
    desc: 'Decoupled web architecture using Strapi, Contentful, Shopify Hydrogen, or custom headless backends for instant content updates and security.'
  },
  {
    icon: <Server size={24} />,
    title: 'REST & GraphQL API Development',
    desc: 'Robust API endpoints connecting web frontends with Tally ERP, payment gateways (Razorpay, Cashfree, Stripe), and external SaaS tools.'
  },
  {
    icon: <Shield size={24} />,
    title: 'SSL, DDoS & Web Security Hardening',
    desc: 'Complete web protection including SSL certificates, CORS policies, Web Application Firewalls (WAF), rate limiting, and zero-vulnerability audits.'
  }
];

const webProcessDetailed = [
  { num: '01', title: 'Scope & User Flow Design', desc: 'Mapping user journeys, sitemap hierarchy, conversion funnels, and selecting between React Client-Side or Next.js Server-Side rendering.' },
  { num: '02', title: 'Figma UI/UX & Responsive Wireframing', desc: 'Designing modern glassmorphic layouts, micro-animations, and responsive desktop/mobile mockups for client approval.' },
  { num: '03', title: 'React / Next.js Component Coding', desc: 'Building modular React components, state hooks, form handlers, and connecting backend Node.js APIs and databases.' },
  { num: '04', title: 'Core Web Vitals & Speed Audit', desc: 'Benchmarking sub-second load speeds, image compression, lazy loading assets, and minifying bundle sizes for 95+ PageSpeed scores.' },
  { num: '05', title: 'SEO Schema & Production Launch', desc: 'Injecting Schema.org JSON-LD, meta tags, sitemap indexing, SSL certificates, and hosting launch on Railway/AWS.' },
  { num: '06', title: 'Post-Launch AMC & Maintenance', desc: 'Providing 30 days of free post-launch support, continuous uptime monitoring, security patching, and content updates.' }
];

const webTech = [
  'React.js (v18)', 'Next.js 14', 'Node.js / Express', 'TypeScript',
  'Tailwind CSS', 'Framer Motion', 'PostgreSQL / MongoDB', 'Railway / Vercel',
  'REST & GraphQL APIs', 'Docker Containers', 'Cloudflare CDN', 'Schema.org JSON-LD'
];

const webFaqsDetailed = [
  { q: 'How is website & web application development done step-by-step?', a: 'We follow 6 stages: (1) Scope & User Flow Mapping, (2) Figma UI/UX Wireframing, (3) React/Next.js Coding, (4) Core Web Vitals Speed Audit, (5) SEO Metadata & Production Launch, and (6) Post-Launch AMC Maintenance.' },
  { q: 'What web frameworks do you use for development?', a: 'We specialize in React.js, Next.js 14 for server-side rendering & static generation, TypeScript, and Node.js/Express for backend APIs.' },
  { q: 'Will our website be fast and mobile-responsive?', a: 'Yes! Every website undergoes rigorous mobile responsiveness testing across smartphones/tablets and is engineered for sub-second page loads.' },
  { q: 'Can you migrate our existing site to a modern React stack?', a: 'Yes, we seamlessly migrate legacy WordPress, PHP, or ASP.NET sites to modern React/Next.js architectures while preserving 100% of your existing SEO rankings.' },
  { q: 'Do we get full source code and admin control?', a: 'Yes! 100% of the source code, GitHub repository, hosting credentials, and admin access belong entirely to your business upon completion.' },
  { q: 'Do you sign an NDA before reviewing our web project?', a: 'Absoltely. We sign strict Non-Disclosure Agreements (NDAs) before reviewing any business specs or project documentation.' },
  { q: 'What is included in post-launch web maintenance?', a: 'All web builds include 30 days of free post-launch support. After that, we offer optional SLA-backed AMC contracts for 24/7 uptime monitoring and updates.' }
];

export default function WebDev() {
  const whatsappUrl = "https://wa.me/918287999236?text=Hi%20Wayug!%20I%20need%20a%20custom%20website%20or%20web%20application.";

  return (
    <section className="web-section">
      <div className="container">
        
        {/* Hero */}
        <motion.div 
          className="web-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="web-badge">
            <Globe size={16} /> High-Performance Web Engineering
          </div>
          <h1 className="web-title">
            Website &amp; Web Application Development in <span className="gradient-text">Delhi NCR</span>
          </h1>
          <p className="web-subtitle">
            Fast, secure, responsive, and SEO-optimized custom web applications, SaaS platforms, and enterprise corporate websites built for high conversion.
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Discuss Your Web Project <ArrowRight size={16} />
            </a>
            <a href="/contact" className="btn btn-outline">
              Get Proposal &amp; Timeline
            </a>
          </div>
        </motion.div>

        {/* Feature Grid */}
        <div className="web-grid">
          {webServices.map((item, idx) => (
            <motion.div 
              key={item.title}
              className="web-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
            >
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(37, 99, 235, 0.1)', color: '#60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                {item.icon}
              </div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* 6-Step Development Process */}
        <div style={{ margin: '80px 0' }}>
          <h2 className="text-center" style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '12px' }}>
            How Web Application Development Is Done: <span className="gradient-text">Our 6-Step Process</span>
          </h2>
          <p className="text-center" style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 40px', fontSize: '1rem', lineHeight: '1.6' }}>
            Here is our engineering methodology ensuring high performance, responsive design, and top SEO Google rankings.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {webProcessDetailed.map((step) => (
              <div key={step.num} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px' }}>
                <span style={{ fontSize: '2rem', fontWeight: '800', color: '#60a5fa' }}>{step.num}</span>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '700', margin: '12px 0 8px', color: '#fff' }}>{step.title}</h4>
                <p style={{ color: '#a3a3a3', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tech Stack List */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '40px', marginBottom: '80px' }}>
          <h3 style={{ fontSize: '1.8rem', fontWeight: '800', textAlign: 'center', marginBottom: '24px' }}>
            Technologies We Master
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            {webTech.map((t) => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e5e5e5', fontSize: '0.95rem' }}>
                <CheckCircle size={16} color="#60a5fa" /> {t}
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <FAQAccordion 
          title="Detailed Web Development FAQs" 
          faqs={webFaqsDetailed} 
          accentColor="#60a5fa" 
        />

        {/* Bottom High-Converting Contact Banner */}
        <div style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(37,99,235,0.3)', borderRadius: '24px', padding: '48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '12px' }}>Ready to Build Your Custom Website or Web App?</h2>
          <p style={{ color: '#a3a3a3', maxWidth: '600px', margin: '0 auto 24px', fontSize: '1rem', lineHeight: '1.6' }}>
            Get a high-speed, SEO-optimized website built by experts. Connect with our engineering team for a free technical consultation.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} /> Chat on WhatsApp (+91 8287999236)
            </a>
            <a href="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <PhoneCall size={16} /> Send Project Inquiry
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
